import React, { FC, memo, useState } from "react";
import { ICohortComment } from "../../stores/jotai/base";
import { Button, Stack, Typography } from "@mui/material";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import ScoringFeedbackHeader from "./ScoringFeedbackHeader";
import moment from "moment";
import { buildCurrentRatings } from "../../util/scoringUtils";
import ScoringRatingPanel from "./ScoringRatingPanel";
import ScoringRatingChip from "./ScoringRatingChip";
import ScoringRating from "./ScoringRating";
import { useTheme,Theme } from '@mui/material/styles';

interface IScoringCommentCard {
    comment?: ICohortComment;
    ratingQuestions?: string[];
}

const ScoringCommentCard: FC<IScoringCommentCard> = (props) => {
    const { comment, ratingQuestions } = props;
    const [showRatingDistribution, setShowRatingDistribution] = useState(false);
    const commentRatings: number[] = buildCurrentRatings(comment);
    const theme = useTheme();
    const avgRating = commentRatings.reduce(
        (sum, rating) => sum + rating / ratingQuestions?.length,
        0
    );

    return (
        <Stack
            sx={{
                backgroundColor: theme?.custom?.white,
                padding: "16px",
                borderRadius: "4px",
                border: `1px solid ${theme?.custom?.borderGrey}`,
            }}
            spacing={2}
        >
            <ScoringFeedbackHeader
                user={comment?.user}
                date={moment(comment?.modified)}
                periodIndex={comment?.iterator}
            />
            <Typography
                sx={{
                    fontStyle: "italic",
                    fontWeight: 500,
                    color: !!comment?.content ? theme?.custom?.text : theme?.custom?.borderGrey,
                    padding: "8px 16px",
                    borderRadius: "4px",
                    border: `1px solid ${theme?.custom?.disabledColor}`,
                    backgroundColor: !!comment?.content
                        ? theme?.custom?.white
                        : theme?.custom?.disabledColor,
                }}
            >
                {comment?.content || "No comment provided."}
            </Typography>
            {comment?.rating && (
                <Stack spacing={4}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ alignItems: "center" }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                color: theme?.custom?.grayLabelText,
                            }}
                        >
                            Rated:
                        </Typography>
                        <ScoringRatingChip
                            rating={avgRating}
                            sx={{
                                marginLeft: "8px !important",
                            }}
                        />
                        <ScoringRating
                            precision={0.5}
                            value={avgRating}
                            readOnly={true}
                        />
                        <Button
                            sx={{ marginLeft: "24px !important" }}
                            onClick={() =>
                                setShowRatingDistribution((prevState) => !prevState)
                            }
                        >
                            {showRatingDistribution ? "Hide" : "Show"} full ratings{" "}
                            <KeyboardArrowUpOutlinedIcon
                                sx={{
                                    marginLeft: "2px",
                                    transform: `rotate(${showRatingDistribution ? 0 : 180}deg)`,
                                    transition: "transform 0.2s ease",
                                }}
                            />
                        </Button>
                    </Stack>
                    {showRatingDistribution && (
                        <ScoringRatingPanel
                            ratings={commentRatings}
                            ratingCriteria={ratingQuestions}
                            headerText="Ratings Distribution"
                            hideRatingChip={true}
                            readOnly={true}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default memo(ScoringCommentCard);
